import { lazy, type ComponentType } from 'react'

/**
 * Marks that we have already force-reloaded to recover a failed chunk import,
 * so a genuinely-broken chunk can't put the tab in a reload loop. sessionStorage
 * (not localStorage) so it dies with the tab.
 */
export const CHUNK_RELOAD_KEY = 'csfbl:chunk-reload'

// Private-browsing modes can throw on storage access. Recovery must not depend
// on the guard existing — losing it is better than never recovering at all.
function readGuard(): boolean {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_KEY) !== null
  } catch {
    return false
  }
}

function writeGuard(): void {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
  } catch {
    /* best effort */
  }
}

function clearGuard(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY)
  } catch {
    /* best effort */
  }
}

/**
 * Loads a lazy route's module, recovering from the stale-chunk failure that a
 * deploy causes.
 *
 * Every deploy replaces all hashed assets — `az webapp deploy --clean true`
 * removes server-side files not in the zip, and prod's staging-slot swap
 * replaces the filesystem wholesale. A tab opened before a deploy is still
 * running the old `index.html` (which is deliberately `no-store`) holding chunk
 * hashes that no longer exist, so its next lazy-route navigation fails to
 * import. One full reload fetches the new `index.html` with valid hashes and
 * lands the user on the page they asked for.
 *
 * On a second consecutive failure the chunk is genuinely broken rather than
 * merely stale, so the error propagates to `RouteErrorBoundary` and the user
 * gets its fallback instead of an endless reload.
 *
 * The `any` in the constraint mirrors React's own
 * `lazy<T extends ComponentType<any>>`. A narrower constraint (`never`,
 * `Record<string, never>`) makes real page components unassignable — `tsc -b`
 * rejects it.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function loadRouteModule<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): Promise<{ default: T }> {
  try {
    const mod = await factory()
    clearGuard()
    return mod
  } catch (error) {
    if (readGuard()) throw error

    writeGuard()
    window.location.reload()

    // The reload is asynchronous. Never settle, so React keeps showing the
    // Suspense fallback instead of briefly rendering a broken route.
    return new Promise<never>(() => {})
  }
}

/**
 * Drop-in replacement for `React.lazy` for route-level code splitting, adding
 * the stale-chunk recovery above. Use this for every routed page.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyRoute<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() => loadRouteModule(factory))
}
